import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { Cart, Product, CartContextType } from '../types';
import { CART_ENABLED } from '../config/features';

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; variantId?: string; quantity: number } }
  | { type: 'SET_ITEM'; payload: { product: Product; variantId?: string; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { productId: string; variantId?: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; variantId?: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_CART'; payload: Cart }
  | { type: 'SET_OPEN'; payload: boolean };

const cartReducer = (state: Cart, action: CartAction): Cart => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, variantId, quantity } = action.payload;
      const currentPrice = variantId ? 
        product.variants?.find(v => v.id === variantId)?.price || product.price || 0 :
        product.price || 0;
      
      const existingItem = state.items.find(item => 
        item.product.id === product.id && item.variantId === variantId
      );
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.product.id === product.id && item.variantId === variantId
              ? { ...item, quantity: item.quantity + quantity }
              : item
          ),
          total: state.total + currentPrice * quantity
        };
      } else {
        return {
          ...state,
          items: [...state.items, { product, variantId, quantity }],
          total: state.total + currentPrice * quantity
        };
      }
    }
    
    case 'SET_ITEM': {
      const { product, variantId, quantity } = action.payload;
      const currentPrice = variantId ? 
        product.variants?.find(v => v.id === variantId)?.price || product.price || 0 :
        product.price || 0;
      
      const existingItem = state.items.find(item => 
        item.product.id === product.id && item.variantId === variantId
      );
      
      if (existingItem) {
        const quantityDiff = quantity - existingItem.quantity;
        return {
          ...state,
          items: state.items.map(item =>
            item.product.id === product.id && item.variantId === variantId
              ? { ...item, quantity }
              : item
          ),
          total: state.total + currentPrice * quantityDiff
        };
      } else {
        return {
          ...state,
          items: [...state.items, { product, variantId, quantity }],
          total: state.total + currentPrice * quantity
        };
      }
    }
    
    case 'REMOVE_ITEM': {
      const { productId, variantId } = action.payload;
      const itemToRemove = state.items.find(item => 
        item.product.id === productId && item.variantId === variantId
      );
      if (!itemToRemove) return state;
      
      const currentPrice = variantId ? 
        itemToRemove.product.variants?.find(v => v.id === variantId)?.price || itemToRemove.product.price || 0 :
        itemToRemove.product.price || 0;
      
      return {
        ...state,
        items: state.items.filter(item => 
          !(item.product.id === productId && item.variantId === variantId)
        ),
        total: state.total - currentPrice * itemToRemove.quantity
      };
    }
    
    case 'UPDATE_QUANTITY': {
      const { productId, variantId, quantity } = action.payload;
      const item = state.items.find(item => 
        item.product.id === productId && item.variantId === variantId
      );
      if (!item) return state;
      
      const quantityDiff = quantity - item.quantity;
      const currentPrice = variantId ? 
        item.product.variants?.find(v => v.id === variantId)?.price || item.product.price || 0 :
        item.product.price || 0;
      
      return {
        ...state,
        items: state.items.map(item =>
          item.product.id === productId && item.variantId === variantId
            ? { ...item, quantity }
            : item
        ),
        total: state.total + currentPrice * quantityDiff
      };
    }
    
    case 'CLEAR_CART':
      return {
        items: [],
        total: 0
      };
      
    case 'SET_CART':
      return action.payload;
      
    default:
      return state;
  }
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Загружаем корзину из localStorage при инициализации
  const getInitialCart = (): Cart => {
    if (!CART_ENABLED) {
      try {
        localStorage.removeItem('proteko-cart');
      } catch (_) {}
      return { items: [], total: 0 };
    }

    try {
      const savedCart = localStorage.getItem('proteko-cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        // Проверяем, что структура корректная
        if (parsedCart.items && Array.isArray(parsedCart.items) && typeof parsedCart.total === 'number') {
          return parsedCart;
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return { items: [], total: 0 };
  };

  const [cart, dispatch] = useReducer(cartReducer, getInitialCart());
  const [isOpen, setIsOpen] = React.useState(false);

  // Сохраняем корзину в localStorage при каждом изменении
  useEffect(() => {
    if (!CART_ENABLED) {
      try {
        localStorage.removeItem('proteko-cart');
      } catch (_) {}
      return;
    }

    try {
      localStorage.setItem('proteko-cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Очищаем корзину при выходе пользователя (слушаем событие storage для синхронизации между вкладками)
  useEffect(() => {
    if (!CART_ENABLED) {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'proteko-cart' && e.newValue) {
        try {
          const newCart = JSON.parse(e.newValue);
          if (newCart.items && Array.isArray(newCart.items) && typeof newCart.total === 'number') {
            dispatch({ type: 'SET_CART', payload: newCart });
          }
        } catch (error) {
          console.error('Error parsing cart from storage event:', error);
        }
      }
    };

    const handleUserSignOut = () => {
      // Очищаем корзину при выходе пользователя
      dispatch({ type: 'CLEAR_CART' });
      localStorage.removeItem('proteko-cart');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-signed-out', handleUserSignOut);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-signed-out', handleUserSignOut);
    };
  }, []);

  const addToCart = (product: Product, variantId: string | undefined, quantity: number) => {
    if (!CART_ENABLED) return;
    dispatch({ type: 'ADD_ITEM', payload: { product, variantId, quantity } });
  };

  const setCartItem = (product: Product, variantId: string | undefined, quantity: number) => {
    if (!CART_ENABLED) return;
    dispatch({ type: 'SET_ITEM', payload: { product, variantId, quantity } });
  };

  const removeFromCart = (productId: string, variantId?: string) => {
    if (!CART_ENABLED) return;
    dispatch({ type: 'REMOVE_ITEM', payload: { productId, variantId } });
  };

  const updateQuantity = (productId: string, variantId: string | undefined, quantity?: number) => {
    if (!CART_ENABLED) return;
    if (!quantity || quantity <= 0) {
      removeFromCart(productId, variantId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, variantId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    try {
      localStorage.removeItem('proteko-cart');
    } catch (_) {}
  };

  const value: CartContextType = {
    cart,
    addToCart,
    setCartItem,
    removeFromCart,
    updateQuantity,
    clearCart,
    isOpen,
    setIsOpen
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
